import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Tag, X } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/Bits";
import { AdCard } from "@/components/AdSlot";
import { useAuth } from "@/context/AuthContext";
import { markOffersSeen } from "@/hooks/useNewOffers";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;

function PartnersStrip({ selected, onSelect, counts }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/ads/partners-strip").then(({ data }) => setRows(data.partners || [])).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <section className="space-y-2" data-testid="partners-strip">
      <p className="mpl-eyebrow">Ils nous soutiennent</p>
      <p className="text-[11px] text-slate-500">Touche un logo pour voir les offres de ce partenaire.</p>
      <ul className="mpl-scroll-x flex gap-3 overflow-x-auto px-1 pb-1 pt-2">
        {rows.map((p) => {
          const on = selected?.id === p.id;
          return (
            <li key={p.id} className="w-24 shrink-0">
              <button type="button" onClick={() => onSelect(on ? null : p)} aria-pressed={on} aria-label={`Voir les offres de ${p.name}`}
                className="flex w-full flex-col items-center gap-1.5 text-center transition-transform hover:-translate-y-0.5" data-testid={`partners-strip-${p.id}`}>
                <span className={`flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-2 ring-2 ring-offset-2 ring-offset-[#05070D] ${on ? "ring-[#B7F52A]" : "ring-transparent"}`}>
                  <img src={`${ORIGIN}${p.logo_url}`} alt={`Logo ${p.name}`} loading="lazy" className="max-h-full max-w-full object-contain" />
                </span>
                <span className={`line-clamp-2 text-[11px] [overflow-wrap:anywhere] ${on ? "font-semibold text-[#B7F52A]" : "text-slate-300"}`}>{p.name}</span>
                {counts[p.id] > 0 && <span className="text-[10px] text-slate-500">{counts[p.id]} offre{counts[p.id] > 1 ? "s" : ""}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function Offers() {
  const [ads, setAds] = useState(null);
  const [selected, setSelected] = useState(null);
  const [partnerAds, setPartnerAds] = useState(null);
  const { user } = useAuth();
  useEffect(() => { if (user?.id) markOffersSeen(user.id); }, [user?.id]);
  useEffect(() => { api.get("/ads/serve", { params: { placement: "offers_page" } }).then(({ data }) => setAds(data.ads || [])).catch(() => setAds([])); }, []);
  useEffect(() => {
    setPartnerAds(null);
    if (!selected) return;
    api.get("/ads/serve", { params: { placement: "offers_page", partner_id: selected.id } }).then(({ data }) => setPartnerAds(data.ads || [])).catch(() => setPartnerAds([]));
  }, [selected]);
  const counts = (ads || []).reduce((acc, a) => ({ ...acc, [a.partner_id]: (acc[a.partner_id] || 0) + 1 }), {});
  const shown = selected ? partnerAds : ads;
  return (
    <div className="space-y-5" data-testid="offers-page">
      <PageHeader eyebrow="Partenaires" title="Offres partenaires" />
      <p className="text-sm text-slate-400">Des offres de nos partenaires, clairement identifiées. Elles n'ont aucun effet sur tes points ni sur les classements. Ma Petite Ligue ne transmet jamais tes coordonnées aux partenaires.</p>
      <PartnersStrip selected={selected} onSelect={setSelected} counts={counts} />
      {selected && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-slate-200" data-testid="offers-filter">
          Offres de <b className="text-white">{selected.name}</b>
          <Link to={`/offres/partenaire/${selected.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#B7F52A] px-2.5 py-0.5 text-xs font-semibold text-[#05070D] hover:brightness-110" data-testid="offers-filter-partner-page">Voir sa page <ArrowRight size={12} /></Link>
          <button type="button" onClick={() => setSelected(null)} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-2 py-0.5 text-xs text-slate-300 hover:bg-white/5" data-testid="offers-filter-clear"><X size={12} /> Toutes les offres</button>
        </p>
      )}
      {!shown ? <Spinner /> : shown.length ? <div className="grid gap-3 md:grid-cols-2">{shown.map((a) => <AdCard key={a.token} ad={a} />)}</div>
        : selected ? <EmptyState icon={Tag} title={`Aucune offre de ${selected.name} pour toi en ce moment`} text="Ses offres actuelles visent peut-être une autre région ou un autre type de ligue." testId="offers-partner-empty" />
        : <EmptyState icon={Tag} title="Aucune offre pour l'instant" text="Les offres partenaires disponibles dans ta région apparaîtront ici." testId="offers-empty" />}
    </div>
  );
}
